/**
 * @file Expert dashboard UI — pages/Dashboard/Expert/AssignedMaterials/ExpertAssignedMaterials.tsx.
 */

// src/pages/Dashboard/Expert/AssignedMaterials/ExpertAssignedMaterials.tsx
import React, { useEffect, useState } from "react";
import axiosClient from "../../../../api/axiosClient";
import publicAxiosClient from "../../../../api/publicAxiosClient";
import { API_URLS } from "../../../../constants/apiUrls";
import Button from "../../../../components/common/Button";
import MaterialCard from "../../../../components/common/MaterialCard";
import AnalysisBanner from "../../../../components/common/AnalysisBanner";
import {
  buildExpertAssignedMaterialsList,
  parseExpertMaterialsResponse,
  pickMaterialImagePath,
} from "../../../../utils/resolveUploadAssetUrl";
import "./ExpertAssignedMaterials.css";

interface Material {
  id: number;
  name: string;
  code: string;
  imageUrl: string | null;
}

interface Props {
  onOpenSubmissions: (materialId: number) => void;
}

const ExpertAssignedMaterials: React.FC<Props> = ({ onOpenSubmissions }) => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMaterials = async () => {
      setLoading(true);
      setError(null);
      try {
        const [expertRes, catalogRes] = await Promise.all([
          axiosClient.get("/expert/materials"),
          axiosClient.get(API_URLS.MATERIALS.LIST).catch(() =>
            publicAxiosClient.get(API_URLS.MATERIALS.LIST).catch(() => ({
              data: { data: [] },
            }))
          ),
        ]);

        if (expertRes.data?.success) {
          const expertRows = parseExpertMaterialsResponse(expertRes.data.data);
          const catalogItems = Array.isArray(catalogRes.data?.data)
            ? catalogRes.data.data
            : Array.isArray(catalogRes.data)
              ? catalogRes.data
              : [];

          setMaterials(buildExpertAssignedMaterialsList(expertRows, catalogItems));
        } else {
          setError("Failed to load materials");
        }
      } catch (err: any) {
        console.error("Error fetching materials:", err);
        setError(err.response?.data?.message || "Failed to load materials");
      } finally {
        setLoading(false);
      }
    };

    fetchMaterials();
  }, []);

  if (loading) {
    return (
      <div className="background-form-container analysis-shell">
        <AnalysisBanner title="My Assigned Materials" />
        <div className="loading-placeholder">
          <div className="loading-spinner"></div>
          <p>Loading assigned materials...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="background-form-container analysis-shell">
        <AnalysisBanner title="My Assigned Materials" />
        <div className="error-placeholder">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Materials</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="background-form-container analysis-shell">
      <AnalysisBanner title="My Assigned Materials" />
      <div className="expert-assigned-materials-container">
        {materials.length === 0 ? (
          <div className="empty-materials">
            <div className="empty-icon">📦</div>
            <h3>No Materials Assigned</h3>
            <p>
              You haven't been assigned any materials yet. Contact an administrator to get
              assigned to materials.
            </p>
          </div>
        ) : (
          <div className="expert-assigned-list">
            {materials.map((material) => (
              <div key={material.id} className="expert-assigned-item">
                <MaterialCard
                  id={material.id}
                  name={material.name}
                  code={material.code}
                  imageUrl={pickMaterialImagePath(material)}
                  showAllDatasetsStat={false}
                  showExploreButton={false}
                  showStats={false}
                  apiBaseUrl={axiosClient.defaults.baseURL}
                />
                <div className="expert-assigned-item__actions">
                  <Button
                    label="View Submissions"
                    onClick={() => onOpenSubmissions(material.id)}
                    variant="default"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/** React component — Expert Assigned Materials. */
export default ExpertAssignedMaterials;
